import { useMutation, useQuery } from '@apollo/client'
import { Button, Grid, TextField, Typography } from '@mui/material'
import ToggleEditMultiSelectField from 'candidate/components/ToggleEditFormFields/ToggleEditMultiSelectField'
import { GetCandidateCompanyPreferenceOptions } from 'candidate/graphql/recruitingPreferences.gql'
import React, { useCallback, useEffect, useState } from 'react'
import useToast from 'shared/components/Toast/useToast'
import { UpdateCandidateCompanyPreferences } from 'shared/graphql/candidatePreferences.gql'
import { DropdownOption } from 'shared/models/dropdownOption'
import { EditButtonContainer } from 'shared/style/styled'
import PreferenceSectionHeader from './PreferenceSectionHeader'
import { RecruitingFormComponentProps } from './preference.types'

const OTHER_VALUE = 'Other'

const getCustomValueForTier = (prefs: any[], order: number): string => {
    const otherPref = prefs.find(
        (p) => p.order === order && p.preferenceCompany.value === OTHER_VALUE
    )
    return otherPref?.customValue || ''
}

const Company: React.FC<RecruitingFormComponentProps> = ({
    candidate,
    handleEditArray,
    editArray,
    sectionName,
    setUnsavedChangesExist,
    refetch,
}) => {
    const [mutate] = useMutation(UpdateCandidateCompanyPreferences)
    const toast = useToast()

    const isEdit =
        editArray.find((ele) => ele.sectionName === sectionName)?.isEdit ||
        false

    const companyPreferences = candidate.companyPreferences ?? []
    const CompaniesOptions = useQuery(GetCandidateCompanyPreferenceOptions)

    const [primaryCompanies, setPrimaryCompanies] = useState<DropdownOption[]>(
        companyPreferences
            .filter(({ order }) => order === 1)
            .map((pref) => pref.preferenceCompany)
    )
    const [secondaryCompanies, setSecondaryCompanies] = useState<
        DropdownOption[]
    >(
        companyPreferences
            .filter(({ order }) => order === 2)
            .map((pref) => pref.preferenceCompany)
    )

    const [customValues, setCustomValues] = useState<Record<number, string>>({
        1: getCustomValueForTier(companyPreferences, 1),
        2: getCustomValueForTier(companyPreferences, 2),
    })

    const resetForm = useCallback(() => {
        setPrimaryCompanies(
            companyPreferences
                .filter(({ order }) => order === 1)
                .map((pref) => pref.preferenceCompany)
        )
        setSecondaryCompanies(
            companyPreferences
                .filter(({ order }) => order === 2)
                .map((pref) => pref.preferenceCompany)
        )
        setCustomValues({
            1: getCustomValueForTier(companyPreferences, 1),
            2: getCustomValueForTier(companyPreferences, 2),
        })
    }, [companyPreferences])

    useEffect(() => {
        resetForm()
    }, [companyPreferences, resetForm])

    useEffect(() => {
        if (!isEdit) resetForm()
    }, [isEdit, resetForm])

    const fromDbPrimary = companyPreferences
        .filter(({ order }) => order === 1)
        .map((pref) => pref.preferenceCompany.id)
    const fromDbSecondary = companyPreferences
        .filter(({ order }) => order === 2)
        .map((pref) => pref.preferenceCompany.id)
    const dbCustom1 = getCustomValueForTier(companyPreferences, 1)
    const dbCustom2 = getCustomValueForTier(companyPreferences, 2)
    const unsavedChangesExist =
        primaryCompanies.length !== fromDbPrimary.length ||
        primaryCompanies.map((c) => c.id).join() !== fromDbPrimary.join() ||
        secondaryCompanies.length !== fromDbSecondary.length ||
        secondaryCompanies.map((c) => c.id).join() !== fromDbSecondary.join() ||
        customValues[1] !== dbCustom1 ||
        customValues[2] !== dbCustom2

    useEffect(() => {
        setUnsavedChangesExist(unsavedChangesExist)
    }, [unsavedChangesExist, setUnsavedChangesExist])

    const handleUpdate = async () => {
        try {
            const buildPreferences = (
                companies: DropdownOption[],
                order: number
            ) =>
                companies.map((company) => ({
                    preferenceCompanyId: company.id,
                    order,
                    ...(company.value === OTHER_VALUE && customValues[order]
                        ? { customValue: customValues[order] }
                        : {}),
                }))
            const preferences = [
                ...buildPreferences(primaryCompanies, 1),
                ...buildPreferences(secondaryCompanies, 2),
            ]
            const { data: mutationResult, errors } = await mutate({
                variables: { candidateId: candidate.id, preferences },
            })
            if (mutationResult?.updateCandidateCompanyPreferences?.success) {
                await handleEditArray(sectionName, true)
                toast({
                    title: 'Success',
                    message: 'Target firm preferences updated',
                    severity: 'success',
                })
                refetch()
                setUnsavedChangesExist(false)
            } else {
                console.log(errors)
                toast({
                    severity: 'error',
                    title: 'Error',
                    message: 'Failed to update target firm preferences',
                })
            }
        } catch (error) {
            console.log(error)
            toast({
                severity: 'error',
                title: 'Error',
                message: 'Failed to update target firm preferences',
            })
        }
    }

    const tiers = [
        {
            label: '1st Choice',
            companies: primaryCompanies,
            setter: setPrimaryCompanies,
            order: 1,
        },
        {
            label: '2nd Choice',
            companies: secondaryCompanies,
            setter: setSecondaryCompanies,
            order: 2,
        },
    ]

    const formatDisplayValue = (v: DropdownOption, order: number) =>
        v.value === OTHER_VALUE && customValues[order]
            ? { ...v, value: `Other: ${customValues[order]}` }
            : v

    return (
        <>
            <PreferenceSectionHeader
                handleEditArray={handleEditArray}
                sectionName={sectionName}
                title="Target Firms"
                subTitle="What firms are you interested in working at?"
            />
            <Grid container spacing={2}>
                {tiers.map(({ label, companies, setter, order }) => (
                    <Grid item xs={6} key={order}>
                        <Typography variant="subtitle2" sx={{ mb: 1 }}>
                            {label}
                        </Typography>
                        <ToggleEditMultiSelectField
                            edit={isEdit}
                            label={label}
                            labelId={`company_preference_${order}`}
                            dropdownData={
                                CompaniesOptions.data?.preferenceCompanies || []
                            }
                            isDisabled={false}
                            onInputChange={(selectedOptions) => {
                                setter(selectedOptions)
                                if (
                                    !selectedOptions.some(
                                        (o) => o.value === OTHER_VALUE
                                    )
                                ) {
                                    setCustomValues((prev) => ({
                                        ...prev,
                                        [order]: '',
                                    }))
                                }
                            }}
                            selectedDropdownData={
                                isEdit
                                    ? companies
                                    : companies.map((v) =>
                                          formatDisplayValue(v, order)
                                      )
                            }
                            limit={3}
                        />
                        {companies.some((c) => c.value === OTHER_VALUE) &&
                            isEdit && (
                                <TextField
                                    fullWidth
                                    label="Enter firm name"
                                    value={customValues[order]}
                                    onChange={(e) =>
                                        setCustomValues((prev) => ({
                                            ...prev,
                                            [order]: e.target.value,
                                        }))
                                    }
                                    sx={{ mb: 2 }}
                                />
                            )}
                    </Grid>
                ))}
                {isEdit && (
                    <EditButtonContainer item xs={12}>
                        <Button
                            variant="outlined"
                            onClick={() => {
                                handleEditArray(sectionName, true)
                                setUnsavedChangesExist(false)
                                resetForm()
                            }}
                        >
                            Cancel
                        </Button>
                        <Button onClick={handleUpdate}>Save and Update</Button>
                    </EditButtonContainer>
                )}
            </Grid>
        </>
    )
}

export default Company
